import React from "react";
import { Link } from "react-router-dom";
import { PilotButton } from "../components/PilotIntake";

function Eyebrow({ children }) {
  return (
    <span
      className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] px-3 py-1.5 rounded-full"
      style={{
        color: "var(--primary-color)",
        backgroundColor: "var(--primary-50)",
        border: "1px solid color-mix(in srgb, var(--primary-color) 14%, transparent)",
      }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "var(--primary-color)" }} />
      {children}
    </span>
  );
}

const PLANS = [
  {
    name: "Instructor Pilot",
    price: "$0",
    cadence: "during pilot · Fall 2026",
    blurb: "Complimentary access for instructors during our limited Fall 2026 pilot. Standard instructor pricing applies after the pilot concludes.",
    features: [
      "Unlimited live polls & Q&A (pilot)",
      "Participation tracking",
      "Session analytics dashboard",
      "Up to 200 students per class",
    ],
    cta: "Apply for pilot",
    to: "/sign-up",
    accent: false,
    badge: "Pilot only",
  },
  {
    name: "Standard",
    price: "$4.99",
    cadence: "per student / semester",
    blurb: "For students who want the full session experience.",
    features: [
      "Real-time Q&A access",
      "Personal analytics board",
      "Grade history & stats",
      "Session replay with answers",
    ],
    cta: "Get started",
    to: "/sign-up",
    accent: true,
  },
  {
    name: "Premium",
    price: "$9.99",
    cadence: "per student / semester",
    blurb: "AI-powered learning, designed for ambitious students.",
    features: [
      "Everything in Standard",
      "AI report after every session",
      "AI playground: quizzes & exams",
      "Priority support",
    ],
    cta: "Coming soon",
    to: "#",
    accent: false,
    soon: true,
  },
];

export default function Pricing() {
  return (
    <main style={{ backgroundColor: "var(--bg-color)", color: "var(--text-color)" }}>
      <section className="pt-20 pb-12" style={{ background: "var(--gradient-soft)" }}>
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <div className="flex justify-center mb-5"><Eyebrow>Pricing</Eyebrow></div>
          <h1 className="text-4xl sm:text-6xl font-semibold tracking-tight" style={{ color: "var(--text-color)" }}>
            Simple, honest{" "}
            <span className="ce-serif italic" style={{ color: "var(--accent-color)" }}>pricing.</span>
          </h1>
          <p className="mt-5 text-lg max-w-2xl mx-auto leading-relaxed" style={{ color: "var(--muted-color)" }}>
            Instructors join through a limited-time pilot. Students pay a small per-semester rate. No seat minimums, no quotes, no hidden fees.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {PLANS.map((p) => (
              <div
                key={p.name}
                className="relative rounded-2xl p-8 flex flex-col"
                style={{
                  backgroundColor: "var(--card-bg)",
                  border: p.accent
                    ? "1px solid color-mix(in srgb, var(--primary-color) 40%, transparent)"
                    : "1px solid var(--border-color)",
                  boxShadow: p.accent ? "var(--shadow-md)" : "var(--shadow-sm)",
                  opacity: p.soon ? 0.85 : 1,
                }}
              >
                {p.accent && (
                  <span
                    className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-semibold uppercase tracking-[0.16em] px-3 py-1 rounded-full"
                    style={{ backgroundColor: "var(--primary-color)", color: "#fff" }}
                  >
                    Most popular
                  </span>
                )}
                {p.badge && !p.accent && (
                  <span
                    className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-semibold uppercase tracking-[0.16em] px-3 py-1 rounded-full"
                    style={{ backgroundColor: "var(--accent-color)", color: "#fff" }}
                  >
                    {p.badge}
                  </span>
                )}
                <div className="text-sm font-semibold uppercase tracking-[0.14em]" style={{ color: "var(--muted-color)" }}>
                  {p.name}
                </div>
                <div className="mt-5 flex items-baseline gap-2">
                  <span className="ce-serif text-5xl tracking-tight" style={{ color: "var(--text-color)" }}>
                    {p.price}
                  </span>
                </div>
                <div className="mt-1 text-sm" style={{ color: "var(--muted-color)" }}>{p.cadence}</div>
                <p className="mt-5 text-sm leading-relaxed" style={{ color: "var(--text-soft)" }}>{p.blurb}</p>

                <ul className="mt-7 space-y-3 text-sm" style={{ color: "var(--text-color)" }}>
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <span className="mt-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full shrink-0"
                        style={{ backgroundColor: "var(--primary-50)", color: "var(--primary-color)" }}>
                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                      </span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 pt-6" style={{ borderTop: "1px solid var(--border-color)" }}>
                  {p.soon ? (
                    <button disabled className="ce-btn ce-btn-secondary w-full" style={{ opacity: 0.6, cursor: "not-allowed" }}>
                      {p.cta}
                    </button>
                  ) : p.name === "Instructor Pilot" ? (
                    <PilotButton className="ce-btn ce-btn-secondary w-full">
                      {p.cta}
                    </PilotButton>
                  ) : (
                    <Link
                      to={p.to}
                      className={`ce-btn w-full ${p.accent ? "ce-btn-primary" : "ce-btn-secondary"}`}
                    >
                      {p.cta}
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-10 text-center text-sm" style={{ color: "var(--muted-color)" }}>
            Need a university-wide license?{" "}
            <Link to="/contact" style={{ color: "var(--primary-color)", fontWeight: 500 }}>Talk to our team →</Link>
          </p>
        </div>
      </section>
    </main>
  );
}
